import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import omit from 'lodash/omit';
import noop from 'lodash/noop';
import { KeyBindingUtil } from 'draft-js';

import getConditionalClasses, {
  CORE_CLASSNAME
} from '@adsk/bim360-matrix-react-helpers/es/getConditionalClasses';

import TextFieldCore from '@adsk/bim360-matrix-react-text-field-core';
import IconButton from '@adsk/bim360-matrix-react-icon-button';
import SvgIcon from '@adsk/bim360-matrix-react-svg-icon';

import cancelInputSvg from '@adsk/bim360-matrix-react-assets/cancel-input.svg';
import approveInputSvg from '@adsk/bim360-matrix-react-assets/approve-input.svg';
import { ThemeConsumer } from '@adsk/bim360-matrix-react-helpers/es/theme';

import './_InlineEditTextField.scss';

const INLINE_EDIT_TEXTFIELD_PROPS = ['onInlineEditStart', 'onInlineEditEnd'];

/**
 * @class InlineEditTextField
 * Same thing as `TextFieldCore` but adds inline editing capabilities, not just for handicapable.
 * It's basically a <span> with super powers.
 *
 * Well... it actually is a span with super powers 🙄
 *
 * Note: You have to use either `onInlineEditEnd` or `onChange` props to get the most recent value.
 * If you plan on changing the font-size/font-weight using className, please use the `inline-edit` mixin provided in `_InlineEditTextFieldMixins.scss`.
 **/
export default class InlineEditTextField extends PureComponent {
  /**
   * @constructor InlineEditTextField
   **/
  constructor(props) {
    super(props);

    this.initialValue = props.value;
    this.internalValue = props.value;
    this.manualBlurAction = null;

    this._handleHoverControl = this._handleHoverControl.bind(this);
    this._handleExitControl = this._handleExitControl.bind(this);
    this._manualApprove = this._manualApprove.bind(this);
    this._manualCancel = this._manualCancel.bind(this);
    this._setTextFieldCoreRef = this._setTextFieldCoreRef.bind(this);

    this.onChange = this.onChange.bind(this);
    this.onFocus = this.onFocus.bind(this);
    this.onBlur = this.onBlur.bind(this);

    this.focus = this.focus.bind(this);
    this.blur = this.blur.bind(this);
    this.select = this.select.bind(this);

    this.state = {
      focused: false,
      value: props.value,
      justClickedControl: false,
      behaviors: this._getBehaviors(props)
    };
  }

  componentDidMount() {
    this.mounted = true;
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.behaviors !== this.props.behaviors) {
      this.setState({ behaviors: this._getBehaviors(nextProps) });
    }

    if (nextProps.value !== this.props.value) {
      this.setState({ value: nextProps.value });
    }

    // In case the value came from a different place and not this component we need to reset the initial value.
    // We can't rely on this.state.value since it might not reflect the value correctly.
    // It might be because `this.setState` is async and by the time it'll change `this.state` it'll be too late.
    // But setting `this.internalValue` is sync.
    // This means the only way for `nextProps.value` & `this.internalValue` to be out of sync is by changing `nextProps.value` from a different source.
    // In this case we assume the user wants to have a new value as the initial value.
    if (this.internalValue !== nextProps.value) {
      this.initialValue = nextProps.value;
      this.internalValue = nextProps.value;
    }
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  onChange(value) {
    this.internalValue = value;
    this.setState({ value });

    if (!this.manualBlurAction) {
      this.props.onChange(value);
    } else {
      // What is this manual blur action thing?
      // This allows to trigger blur manually and specify an action to perform, probably _approveInlineEdit or _cancelInlineEdit.
      // Then why are we clearing it only here? why not after the blur has happened? Good question.
      // That's because onChange for `TextFieldCore` is being called on after the setState of the text has finished.
      // And manual blurs occur during escape/enter which trigger onChange in draft.js.
      // Which means that an internal onChange is happening in TextFieldCore, then onBlur (due to escape/enter), and lastly this onChange callback.
      // That's why we reset it here and don't actually consider it as a change because we already handled it.
      this.manualBlurAction = null;
    }
  }

  onFocus(...args) {
    this.setState({ focused: true });
    this.props.onInlineEditStart(this.state.value);
    this.props.onFocus(...args);
  }

  onBlur(...args) {
    this.setState({ focused: false });

    if (this.manualBlurAction) {
      this.manualBlurAction();
    } else if (!this.aboutToClickControl) {
      this._approveIfValid();
    } else {
      // We assume the user has just clicked a control but we can't know for sure (the mouse up event wasn't on the control)
      // In that case we make sure that the inline edit is no longer in focus state.
      //
      // We do this entire trick just to make sure the onClick is called for the controls.
      // If we make the disappear (by setting width: 0, display: none) immediately on blur the onClick doesn't get triggered.
      // Probably a better solution would be to position the controls differently and only hide them visually (visibility: hidden)
      // But couldn't get it to work :(
      this.setState({ justClickedControl: true });
      setTimeout(() => {
        // this.mounted is used to guard against triggering actions when the component is unmounted (for example when rendering this component as part of Editable)
        // the this.state.justClickedControl is used to know if the control wasn't actually clicked because it wasn't reset by any control to false.
        if (this.mounted && this.state.justClickedControl) {
          this.setState({ justClickedControl: false });
          this._approveIfValid();
        }
      }, 100);
    }

    this.props.onBlur(this.state.value, ...args);
  }

  _approveIfValid(inlineEditFinisher) {
    if (this.props.valid) {
      this._approveInlineEdit(inlineEditFinisher);
    } else {
      this._invalidInlineEdit();
    }
  }

  // This looks very similar to _approveInlineEdit except we don't change the initial value.
  // Also, this looks very similar to _cancelInlineEdit except we don't revert to the initial value.
  // Basically on invalid cancel we want to keep the invalid text and not change the initial value.
  _invalidInlineEdit() {
    this.setState({ justClickedControl: false });
    this.props.onInlineEditEnd(this.state.value, false, this.initialValue, null, false);
  }

  _cancelInlineEdit() {
    this.setState({ value: this.initialValue, justClickedControl: false });
    this.props.onChange(this.initialValue);
    this.props.onInlineEditEnd(this.initialValue, true, this.initialValue, null, true);
  }

  _approveInlineEdit(inlineEditFinisher) {
    this.props.onInlineEditEnd(
      this.state.value,
      false,
      this.initialValue,
      inlineEditFinisher,
      true
    );
    this.initialValue = this.state.value;
    this.setState({ justClickedControl: false });
  }

  _manualApprove(inlineEditFinisher) {
    this.manualBlurAction = () => this._approveIfValid(inlineEditFinisher);
    this.blur();
  }

  _manualCancel() {
    this.manualBlurAction = () => this._cancelInlineEdit();
    this.blur();
  }

  _handleHoverControl() {
    this.aboutToClickControl = true;
  }

  _handleExitControl() {
    this.aboutToClickControl = false;
  }

  _handleReturn(originalHandler = () => 'not-handled') {
    return (event, ...args) => {
      if (!KeyBindingUtil.hasCommandModifier(event) && !event.shiftKey) {
        this._manualApprove(this._getKeyFinisher(event.key));
        return 'handled';
      }
      return originalHandler(event, ...args);
    };
  }

  _getKeyFinisher(key) {
    switch (key) {
      case 'Enter':
        return InlineEditTextField.InlineEditFinishers.ENTER;
      default:
        return key;
    }
  }

  focus() {
    this.textFieldCore && this.textFieldCore.focus();
  }

  blur() {
    this.textFieldCore && this.textFieldCore.blur();
  }

  select() {
    this.textFieldCore && this.textFieldCore.select();
  }

  _getBehaviors(props) {
    // Overriding only the behaviors defined in TextFieldCore.
    // Which means you can still pass behaviors to InlineEditTextField, and they'll work as expected.
    return Object.keys(props.behaviors).reduce((behaviors, behavior) => {
      const textFieldBehavior = TextFieldCore.DefaultBehaviors[behavior];
      if (textFieldBehavior) {
        behaviors[behavior] = {
          ...textFieldBehavior,
          handlers: {
            ...textFieldBehavior.handlers,
            handleReturn: this._handleReturn(textFieldBehavior.handlers.handleReturn),
            onEscape: this._manualCancel
          }
        };
      } else {
        behaviors[behavior] = props.behaviors[behavior];
      }

      return behaviors;
    }, {});
  }

  _setTextFieldCoreRef(textFieldCoreRef) {
    this.textFieldCore = textFieldCoreRef;
  }

  /**
   * Render
   * @returns {VDOM}
   **/
  render() {
    const approveButtonClasses = classnames('InlineEditTextField__approve-button', {
      'InlineEditTextField__approve-button--invalid': !this.props.valid
    });
    const cancelButtonClasses = classnames('InlineEditTextField__cancel-button', {
      'InlineEditTextField__cancel-button--invalid': !this.props.valid
    });
    const controlsClasses = classnames('InlineEditTextField__controls', {
      'InlineEditTextField__controls--focused': this.state.focused || this.state.justClickedControl
    });

    return (
      <ThemeConsumer>
        {({ theme }) => (
          <div
            className={classnames(
              ['InlineEditTextField', `InlineEditTextField--${theme}`, this.props.className],
              getConditionalClasses(['InlineEditTextField', this.props.className], {
                [`${CORE_CLASSNAME}--focused`]: this.state.focused,
                [`${CORE_CLASSNAME}--disabled`]: this.props.disabled,
                [`${CORE_CLASSNAME}--invalid`]: !this.props.valid
              })
            )}
            style={this.props.style}
          >
            <TextFieldCore
              {...omit(this.props, INLINE_EDIT_TEXTFIELD_PROPS)}
              ref={this._setTextFieldCoreRef}
              behaviors={this.state.behaviors}
              value={this.state.value}
              className="InlineEditTextField__text-field-core"
              onChange={this.onChange}
              onFocus={this.onFocus}
              onBlur={this.onBlur}
            />
            <div className={controlsClasses}>
              <IconButton
                tabIndex="-1"
                className={cancelButtonClasses}
                data-testid="InlineEditTextField__cancel-button"
                onMouseDown={this._manualCancel}
                onMouseEnter={this._handleHoverControl}
                onMouseLeave={this._handleExitControl}
              >
                <SvgIcon>{cancelInputSvg}</SvgIcon>
              </IconButton>
              <IconButton
                disabled={!this.props.valid}
                tabIndex="-1"
                className={approveButtonClasses}
                data-testid="InlineEditTextField__approve-button"
                onMouseDown={this._manualApprove}
                onMouseEnter={this._handleHoverControl}
                onMouseLeave={this._handleExitControl}
              >
                <SvgIcon>{approveInputSvg}</SvgIcon>
              </IconButton>
            </div>
          </div>
        )}
      </ThemeConsumer>
    );
  }
}

InlineEditTextField.InlineEditFinishers = {
  ENTER: 'ENTER'
};

InlineEditTextField.defaultProps = {
  ...TextFieldCore.defaultProps,
  onInlineEditStart: noop,
  onInlineEditEnd: noop
};

InlineEditTextField.propTypes = {
  ...TextFieldCore.propTypes,
  /**
   * Callback that's being called when inline editing has started (due to focus)
   * (text) => {}
   */
  onInlineEditStart: PropTypes.func,
  /**
   * Callback that's being called when inline editing has ended (due to blur or clicking one of the controls)
   * (text, isCancel, previousValue, keyboardFinisherKey, isValid) => {}
   */
  onInlineEditEnd: PropTypes.func
};
